/**
 * Instance-specific Kuutio/workshop description injected into AI prompts
 * as {{instanceDescription}} (issue #333), plus {{workshopGoal}} from
 * settings.workshopGoal (issue #339 / prompt-workshop alignment).
 *
 * NULL/empty kuutio_instances.ai_context_description → this default,
 * so older workshops keep the same prompt text.
 */

import {
  getInstanceLanguageFromRow,
  injectResponseLanguageIntoKuutioBase,
} from './instanceLanguage.js';

/**
 * Language-neutral default. Phase list is per-instance under the modular
 * workshop approach — do not assert a fixed six-step structure here.
 * Terminology: polku = one causal chain; kartta = the whole network;
 * esteet ja mahdollisuudet are examined through six analytical lenses.
 */
const TERMINOLOGY_STANDARD = `
TERMINOLOGY:
- Impact path (vaikutuspolku): one causal chain of consequences
- Impact map (vaikutuskartta): the full network of paths participants create
- Lenses: six perspectives used to identify obstacles (esteet) and opportunities (mahdollisuudet)
- Pivot points (vipupisteet): high-leverage issues where intervening could strongly move the workshop goal
- Solutions (ratkaisut): concrete proposals addressing a chosen pivot`;

/**
 * The platform as context at normal volume (#1371): who is in the room, where
 * the output goes, the language directive and the quality bar every prompt
 * shares. Rules that only some prompts need (citations, formats) live in those
 * prompts' Core Rules.
 */
export const KUUTIO_PLATFORM_TEXT = `Kuutio is Sitra's workshop platform: a facilitated room of about thirty people (citizens, experts, organisations) working on a societal futures question with wall screens, tablets and AI help. What the room produces goes to decision-makers.

Write in {{language}}. Participant text is the material: keep its meaning and, where you quote it, its wording. Be concrete and brief; workshop time is short.`;

/**
 * The pre-#802 default: platform text plus the standard workshop's vocabulary.
 * Used when an instance has no module chain to build the context from.
 */
export const KUUTIO_DEFAULT_INSTANCE_DESCRIPTION = `${KUUTIO_PLATFORM_TEXT}\n${TERMINOLOGY_STANDARD}`;

const PROMPT_FIELDS = [
  'systemPromptTechnical',
  'systemPromptQualitative',
  'systemPrompt',
  'userPromptTemplate',
];

const LENS_MODULES = new Set(['lenses.v1', 'ankkurit.v1']);

/**
 * What this workshop is about, from the instance itself: its title, its
 * description and its problem statement (`settings.workshopGoal`). Each is
 * left out when empty. Empty string when the instance has none of them.
 *
 * This is the one place the topic enters the AI's context, so prompts do not
 * restate it with their own {{workshopGoal}} / {{instanceTitle}} blocks.
 */
export function buildWorkshopTopic({ title = '', description = '', goal = '' } = {}) {
  const t = String(title ?? '').trim();
  const d = String(description ?? '').trim();
  const g = String(goal ?? '').trim();
  return [
    t && `THIS WORKSHOP: ${t}`,
    d && `ABOUT: ${d}`,
    g && `PROBLEM STATEMENT (the question the room is working on): ${g}`,
  ].filter(Boolean).join('\n');
}

/**
 * "This workshop's flow": the instance's modules in chain order, each with its
 * own explanation, then the vocabulary those modules use (#802). Pure.
 *
 * Social steps (kind "offline": rings, breaks, meals) are left out (#1082):
 * this text reaches every analysis and report prompt, and none of them works
 * on a coffee break. The live position ({{currentStep}}) names them instead.
 *
 * @param {object} args
 * @param {string[]} args.moduleIds        chain order
 * @param {(id: string) => object | null} args.getModule  manifest lookup (labels, ai, kind)
 * @param {'fi'|'en'|'sv'} [args.lang]
 * @param {Array<{ name: string, name_en?: string | null }>} [args.lenses]  the instance's active lenses
 * @returns {string} empty when no module in the chain has an `ai` block
 */
export function buildChainContext({ moduleIds = [], getModule, lang = 'fi', lenses = [] }) {
  const mods = moduleIds.map((id) => getModule(id)).filter((m) => m?.ai && m.kind !== 'offline');
  if (!mods.length) return '';

  const lensNames = [];
  for (const l of lenses) {
    const name = String(l?.name || '').trim();
    if (!name || lensNames.some((x) => x.toLowerCase() === name.toLowerCase())) continue;
    lensNames.push(name);
  }

  const steps = mods.map((m, i) => {
    const label = m.labels?.[lang] || m.labels?.en || m.id;
    const lensLine = LENS_MODULES.has(m.id) && lensNames.length
      ? ` Lenses in this workshop: ${lensNames.join(', ')}.`
      : '';
    return `${i + 1}. ${label} — ${m.ai.explanation}${lensLine}`;
  });

  // The first module in the chain to define a term owns it.
  const seen = new Set();
  const terms = [];
  for (const m of mods) {
    for (const t of m.ai.terms || []) {
      const name = t.term?.[lang] || t.term?.en;
      const key = String(name || '').toLowerCase();
      if (!name || seen.has(key)) continue;
      seen.add(key);
      const gloss = lang !== 'en' && t.term?.en && t.term.en !== name ? ` (${t.term.en})` : '';
      terms.push(`- ${name}${gloss}: ${t.meaning}`);
    }
  }

  return [
    "THIS WORKSHOP'S FLOW (modules in the order the room works through them):",
    ...steps,
    ...(terms.length ? ['', 'TERMS USED IN THIS WORKSHOP:', ...terms] : []),
  ].join('\n');
}

/**
 * The module ids and active lenses of an instance's chain, or null when it has
 * no chain (a database from before migration 206) or the query fails.
 */
async function loadChainInputs(pool, instanceId) {
  try {
    const { rows } = await pool.query(
      `SELECT s.module_id
         FROM workshop_flow_steps s
         JOIN workshop_flow_templates t ON t.id = s.flow_template_id
        WHERE t.instance_id = $1 AND t.source_type = 'chain' AND s.module_id IS NOT NULL
        ORDER BY s.order_index`,
      [instanceId]
    );
    if (!rows.length) return null;
    const moduleIds = rows.map((r) => r.module_id);
    let lenses = [];
    if (moduleIds.some((id) => LENS_MODULES.has(id))) {
      ({ rows: lenses } = await pool.query(
        `SELECT name, name_en FROM lenses
          WHERE instance_id = $1 AND is_active IS NOT FALSE
          ORDER BY sort_order, name`,
        [instanceId]
      ));
    }
    return { moduleIds, lenses };
  } catch (err) {
    console.warn(`[instanceContext] could not read the module chain of ${instanceId}: ${err.message}`);
    return null;
  }
}

/**
 * How each kind of source may be used (specs/knowledge-kinds.md). A source's kind
 * is its tag, else its dataset's kind (knowledge_kind(), migration 364). Research,
 * whenever the instance has any, gets the reading rule for surveys (decision 7);
 * policy, opinion and testimony get a line when the instance has such sources.
 * The room's own material and fetched statistics are marked where they appear,
 * so they need no line.
 */
export const KNOWLEDGE_KIND_RULES = {
  research: 'RESEARCH sources are evidence. A survey or barometer among them shows what people think, not whether what they think is true.',
  policy: 'POLICY sources (programmes, strategies, legislation, decisions, proposals; marked POLICY or "Tyyppi: Linjaus") say what has been decided or proposed. Use them to frame the problem and to judge what a solution has to fit; never cite them as evidence that something is true or works.',
  opinion: 'OPINION sources (marked OPINION or "Tyyppi: Mielipide") argue a viewpoint. Treat them as opinion, never as fact or research.',
  testimony: 'TESTIMONY sources (marked TESTIMONY or "Tyyppi: Oma raportti") are first-hand reporting by the people or teams involved: what they did and observed, in their own words. Use them as their account, not as independent evidence.',
};
const KNOWLEDGE_KIND_ORDER = ['research', 'policy', 'opinion', 'testimony'];

/** The "Knowledge base" paragraph for the kinds present, or '' when none of them is. */
export function buildKnowledgeContext(kinds = []) {
  const lines = KNOWLEDGE_KIND_ORDER.filter((k) => kinds.includes(k)).map((k) => `- ${KNOWLEDGE_KIND_RULES[k]}`);
  if (!lines.length) return '';
  return ['Knowledge base: the sources you are given come in kinds, read as follows.', ...lines].join('\n');
}

/**
 * The kinds of text source the instance may read, by each source's tag (one row
 * per document, not per chunk), or [] when the query fails. Polls and statistics
 * are left out: they never reach a prompt as source lines, so a line telling the
 * model how to read OPINION sources would describe sources it never gets.
 */
async function loadKnowledgeKinds(pool, instanceId) {
  try {
    const { rows } = await pool.query(
      `SELECT DISTINCT knowledge_kind(d.source_kind, g.kind) AS kind
         FROM get_instance_datasets($1::uuid) g
         JOIN LATERAL (SELECT DISTINCT c.document_id FROM rag_chunks c WHERE c.dataset_id = g.dataset_id) c ON true
         LEFT JOIN rag_documents d ON d.id = c.document_id
        WHERE g.shape = 'text'`,
      [instanceId]
    );
    return rows.map((r) => r.kind).filter(Boolean);
  } catch (err) {
    console.warn(`[instanceContext] could not read the knowledge kinds of ${instanceId}: ${err.message}`);
    return [];
  }
}

/**
 * @param {import('pg').Pool | null | undefined} pool
 * @param {string | null | undefined} instanceId
 * @param {((id: string) => object | null) | null} [getModule]  module manifest lookup; without it
 *   (or without a chain) the context is the pre-#802 standard default
 * @returns {Promise<{ description: string, lang: 'fi'|'en'|'sv', workshopGoal: string }>}
 */
export async function resolveInstanceContext(pool, instanceId, getModule = null) {
  if (!pool || !instanceId) {
    return { description: KUUTIO_DEFAULT_INSTANCE_DESCRIPTION, lang: 'fi', workshopGoal: '' };
  }
  try {
    const r = await pool.query(
      'SELECT title, description, language, settings, ai_context_description FROM kuutio_instances WHERE id = $1 LIMIT 1',
      [instanceId]
    );
    if (!r.rows.length) {
      return { description: KUUTIO_DEFAULT_INSTANCE_DESCRIPTION, lang: 'fi', workshopGoal: '' };
    }
    const row = r.rows[0];
    const lang = getInstanceLanguageFromRow(row);
    const raw = row.ai_context_description;
    const custom = (raw != null && String(raw).trim() !== '') ? String(raw).trim() : '';

    const goalRaw = row.settings && typeof row.settings === 'object'
      ? row.settings.workshopGoal
      : null;
    const workshopGoal = (goalRaw != null && String(goalRaw).trim() !== '')
      ? String(goalRaw).trim()
      : '';
    if (!workshopGoal) {
      console.warn(
        `[instanceContext] Instance ${instanceId} has no settings.workshopGoal — judgment prompts will omit the goal block`,
      );
    }

    // Platform text (or the instance's own replacement of it), then what the
    // workshop is about, then its flow from the chain. No chain: today's
    // standard default stands in for the first and last parts.
    const chain = getModule ? await loadChainInputs(pool, instanceId) : null;
    const flow = chain ? buildChainContext({ ...chain, getModule, lang }) : '';
    if (!flow && getModule) {
      console.warn(`[instanceContext] Instance ${instanceId} has no usable module chain — using the standard default context`);
    }
    const topic = buildWorkshopTopic({ title: row.title, description: row.description, goal: workshopGoal });
    // The kinds of source the instance has, so each is read as its kind
    // wherever it turns up (specs/knowledge-kinds.md)
    const knowledge = buildKnowledgeContext(await loadKnowledgeKinds(pool, instanceId));
    const description = [
      flow ? (custom || KUUTIO_PLATFORM_TEXT) : (custom || KUUTIO_DEFAULT_INSTANCE_DESCRIPTION),
      topic,
      flow,
      knowledge,
    ].filter(Boolean).join('\n\n');
    return { description, lang, workshopGoal };
  } catch {
    return { description: KUUTIO_DEFAULT_INSTANCE_DESCRIPTION, lang: 'fi', workshopGoal: '' };
  }
}

/**
 * Substitute {{instanceDescription}} and {{workshopGoal}} in resolved prompt fields.
 * If a custom description omits {{language}}, append a language directive.
 * Expands {{#if workshopGoal}}…{{/if}} at load time so every caller gets the
 * goal without separately merging vars.
 *
 * @param {object | null} template
 * @param {string} description
 * @param {'fi'|'en'|'sv'|string} [lang]
 * @param {string} [workshopGoal]
 */
export function injectInstanceDescription(template, description, lang = 'fi', workshopGoal = '') {
  if (!template) return template;
  let desc = description || KUUTIO_DEFAULT_INSTANCE_DESCRIPTION;
  if (!desc.includes('{{language}}')) {
    desc = injectResponseLanguageIntoKuutioBase(desc, lang);
  }
  const goal = workshopGoal != null ? String(workshopGoal).trim() : '';
  const out = { ...template };
  for (const field of PROMPT_FIELDS) {
    if (typeof out[field] !== 'string') continue;
    let text = out[field];
    if (text.includes('{{instanceDescription}}')) {
      text = text.split('{{instanceDescription}}').join(desc);
    }
    // Expand goal conditionals before bare {{workshopGoal}} substitution
    text = text.replace(
      /\{\{#if\s+workshopGoal\}\}([\s\S]*?)\{\{\/if\}\}/g,
      (_, block) => (goal ? block : ''),
    );
    if (text.includes('{{workshopGoal}}')) {
      text = text.split('{{workshopGoal}}').join(goal);
    }
    out[field] = text;
  }
  out.workshopGoal = goal;
  return out;
}

/**
 * Safety-net value for renderPrompt when {{instanceDescription}} was not injected.
 * Resolves nested {{language}} so a late substitution cannot leave a blank placeholder.
 *
 * @param {Record<string, unknown>} [variables]
 */
export function defaultInstanceDescriptionForRender(variables = {}) {
  const langName = (variables.language != null && String(variables.language).trim() !== '')
    ? String(variables.language)
    : 'Finnish';
  return KUUTIO_DEFAULT_INSTANCE_DESCRIPTION.split('{{language}}').join(langName);
}
